import { useCallback, useMemo } from 'react';
import { BudgetScenarioPartnerLocationOption } from '@callmart/types';
import { useTranslation } from 'react-i18next';
import { DraftUpdater, ScenarioDraftEntity } from './scenarioDraft';
import { formatPercent, parseBudgetCell } from './helpers';
import ScenarioValueGrid, { ScenarioValueEntity } from './ScenarioValueGrid';

interface ScenarioMinutePriceGridProps {
    months: string[];
    /** The partner/locations the scope multiselect picked — one row each. */
    entityIds: number[];
    draft: ScenarioDraftEntity[];
    onChange: (update: DraftUpdater<ScenarioDraftEntity[]>) => void;
    availablePartnerLocations: BudgetScenarioPartnerLocationOption[];
    isLoading: boolean;
    canEdit: boolean;
}

// Input grid for a Minute-price scenario: rows are partner/locations, cells are monthly percentage
// changes (may be negative). Everything mechanical lives in ScenarioValueGrid (CALL-783); this maps the
// location shape onto the shared entity shape and owns the percent cell format.
const ScenarioMinutePriceGrid = ({
    months,
    entityIds,
    draft,
    onChange,
    availablePartnerLocations,
    isLoading,
    canEdit,
}: ScenarioMinutePriceGridProps) => {
    const { t } = useTranslation('budget');

    const entities: ScenarioValueEntity[] = useMemo(
        () =>
            availablePartnerLocations.map((option) => ({
                entityId: option.locationId,
                entityLabel: option.label,
            })),
        [availablePartnerLocations]
    );

    const parseValue = useCallback((rawValue: unknown) => parseBudgetCell(rawValue), []);

    return (
        <ScenarioValueGrid
            months={months}
            entityIds={entityIds}
            draft={draft}
            onChange={onChange}
            entities={entities}
            isLoading={isLoading}
            canEdit={canEdit}
            entityHeader={t('field.partnerLocation')}
            formatValue={formatPercent}
            parseValue={parseValue}
            labels={{ noRows: t('scenarioDetail.noPartnerLocations') }}
        />
    );
};

export default ScenarioMinutePriceGrid;
